import { Button } from "@pdfking/components"

import { useTranslationTyped } from "../i18n/useTranslation"
import { RangeControl } from "./RangeControl"

// The prev / page-input / last-page / next stepper shared verbatim by both
// viewers — each supplies its own bounds (flat pages vs 3D frames). `max <= 0`
// (nothing loaded, or the book still booting) renders nothing.
export function Pager({
  max,
  min,
  on_value,
  step = 1,
  value,
}: {
  max: number
  min: number
  on_value: (n: number) => void
  // Pages advanced per prev/next click — 2 when the plot pairs pages into
  // spreads, so one click steps a whole spread.
  step?: number
  value: number
}) {
  const total = max - min + 1
  if (total <= 1) return undefined

  // With only 2 pages, prev/next chevrons would just toggle between the same
  // two values — a pair of page-number buttons is more direct. Derived from the
  // real bounds (`[min, max]`), not literal 1/2: 3D frame ranges are 0-based.
  if (total === 2) {
    return (
      <div className="flex items-center gap-1">
        {[min, max].map(page => (
          <Button
            className="h-6 w-auto px-1 text-sm font-normal text-muted-foreground"
            disabled={value === page}
            key={page}
            onClick={() => on_value(page)}
            size="icon-xs"
            variant="ghost"
          >
            {page}
          </Button>
        ))}
      </div>
    )
  }

  const pdfview = useTranslationTyped("pdfview")
  const show_start_end = total > 4

  return (
    <RangeControl
      end_description={show_start_end ? pdfview.LAST_PAGE() : undefined}
      input_class_name="w-8"
      max={max}
      min={min}
      next_description={pdfview.NEXT_PAGE()}
      on_value={on_value}
      previous_description={pdfview.PREV_PAGE()}
      start_description={show_start_end ? pdfview.FIRST_PAGE() : undefined}
      step={step}
      value={value}
      tooltip_side="bottom"
    >
      <Button
        className="h-6 w-auto px-1 text-sm font-normal text-muted-foreground"
        disabled={value >= max}
        onClick={() => on_value(max)}
        size="icon-xs"
        variant="ghost"
      >
        {max}
      </Button>
    </RangeControl>
  )
}
